import { Button } from 'antd'
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  PointerEvent as ReactPointerEvent,
} from 'react'
import styled from 'styled-components'
import classNames from 'classnames'

import 'react-h5-audio-player/lib/styles.css'

import { IconPlayerPlay, IconPlayerStop } from '@/components/common/Icons'
import { poppins } from '@/components/common/fonts'

import { useHumanReadableDuration } from '../../common/hooks/duration'

export const AudioPlayer: FC<{
  src?: string
  isLoading?: boolean
  disabled?: boolean

  className?: string
}> = ({ src, isLoading, disabled, className }) => {
  const audioRef = useRef<HTMLAudioElement>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [isSeeking, setIsSeeking] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)

  const humanReadableDuration = useHumanReadableDuration(duration)
  const humanReadableCurrentTime = useHumanReadableDuration(currentTime)

  const progress = useMemo(() => {
    if (duration === 0) {
      return 0
    }

    return Math.min(Math.max((currentTime / duration) * 100, 0), 100)
  }, [duration, currentTime])

  /**
   * 오디오 컨트롤
   */
  const stop = () => {
    if (!audioRef.current) {
      return
    }

    audioRef.current.pause()
    audioRef.current.currentTime = 0
  }

  const pause = () => {
    audioRef.current?.pause()
  }

  const play = () => {
    audioRef.current?.play()
  }

  const seek = (time: number) => {
    if (!audioRef.current) {
      return
    }

    audioRef.current.currentTime = time
  }

  /**
   * 오디오 진행 상태 등 감지
   */
  useEffect(() => {
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleTimeUpdate = () => {
      setDuration(audioRef.current?.duration || 0)
      setCurrentTime(audioRef.current?.currentTime || 0)
    }
    const handleDurationChange = () =>
      setDuration(audioRef.current?.duration || 0)

    const handleLoadeddata = () => play()

    const audioEl = audioRef.current

    if (audioEl) {
      audioEl.addEventListener('play', handlePlay)
      audioEl.addEventListener('pause', handlePause)
      audioEl.addEventListener('timeupdate', handleTimeUpdate)
      audioEl.addEventListener('durationchange', handleDurationChange)
      audioEl.addEventListener('loadedmetadata', handleDurationChange)
      audioEl.addEventListener('loadeddata', handleLoadeddata)
    }

    return () => {
      if (audioEl) {
        audioEl.removeEventListener('play', handlePlay)
        audioEl.removeEventListener('pause', handlePause)
        audioEl.removeEventListener('timeupdate', handleTimeUpdate)
        audioEl.removeEventListener('durationchange', handleDurationChange)
        audioEl.removeEventListener('loadedmetadata', handleDurationChange)
        audioEl.removeEventListener('loadeddata', handleLoadeddata)
      }
    }
  }, [])

  /**
   * 외부 상태에 따라 오디오 정지
   */
  useEffect(() => {
    if (!isPlaying) {
      return
    }

    if (isLoading || disabled) {
      stop()
    }
  }, [isLoading, disabled, isPlaying])

  /**
   * 이벤트 핸들러
   */
  const handleClickTogglePlay = () => {
    if (isPlaying) {
      audioRef.current?.pause()
    } else {
      audioRef.current?.play()
    }
  }

  const handleSeek = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!audioRef.current) {
      return
    }

    e.preventDefault()

    setIsSeeking(true)

    const target = e.currentTarget

    const seekByMouseClientX = (clientX: number) => {
      const rect = target.getBoundingClientRect()
      const width = rect.width

      const x = clientX - rect.left
      const seekTime =
        (audioRef.current?.duration ?? 0) * Math.min(Math.max(x / width, 0), 1)

      seek(seekTime)
    }

    seekByMouseClientX(e.clientX)

    const wasPaused = audioRef.current.paused

    pause()

    const handlePointerMove = (_e: PointerEvent) => {
      seekByMouseClientX(_e.clientX)
    }

    const handlePointerUp = () => {
      if (!wasPaused) {
        play()
      }

      setIsSeeking(false)

      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
    }

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerUp)
  }, [])

  return (
    <AudioPlayerContainer
      className={classNames(className, {
        disabled: !src || !!disabled,
      })}
    >
      <PlayToggleButton onClick={handleClickTogglePlay}>
        {isPlaying ? <IconPlayerStop /> : <IconPlayerPlay />}
      </PlayToggleButton>

      <CurrentTime>{humanReadableCurrentTime}</CurrentTime>

      <Progress
        onPointerDown={handleSeek}
        onContextMenu={(e) => e.preventDefault()}
      >
        <ProgressBackground />

        <ProgressBar
          style={{
            width: `${progress}%`,
          }}
        >
          <CurrentTimeOverProgressBar
            className={classNames({
              seeking: isSeeking,
            })}
          >
            {humanReadableCurrentTime}
          </CurrentTimeOverProgressBar>
        </ProgressBar>
      </Progress>

      <Duration>{humanReadableDuration}</Duration>

      <audio
        src={src}
        ref={audioRef}
        style={{
          display: 'none',
        }}
      />
    </AudioPlayerContainer>
  )
}

/**
 * &.disabled
 */
const AudioPlayerContainer = styled.div`
  flex: 1;

  display: flex;
  align-items: center;

  height: 70px;
  padding-left: 8px;

  box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.2);

  transition: opacity 0.3s;

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;

    & * {
      pointer-events: none;
    }
  }
`

const PlayToggleButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;

  width: 40px;
  height: 40px;
  margin-right: 10px;

  border: 0;
  box-shadow: none;

  &&&:hover {
    background-color: #f0f0f0;
  }

  path {
    fill: #808080;
  }
`

const CurrentTime = styled.div`
  margin-right: 12px;

  font-family: ${poppins.string};
  font-size: 13px;
  line-height: 20px;
  color: #808080;
`

const Progress = styled.div`
  flex: 1;

  position: relative;

  height: 20px;
  margin-right: 14px;

  cursor: pointer;

  & * {
    pointer-events: none;
  }
`

const ProgressBackground = styled.div`
  position: absolute;

  top: 50%;
  left: 0;

  width: 100%;
  height: 4px;
  border-radius: 2px;
  transform: translateY(-50%);

  background-color: #e5e5e5;
`

const ProgressBar = styled.div`
  position: absolute;

  top: 50%;
  left: 0;

  height: 4px;
  min-width: 0;
  max-width: 100%;
  border-radius: 2px;
  transform: translateY(-50%);

  background-color: #146ef5;

  &::before {
    content: '';

    position: absolute;

    top: 50%;
    right: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    transform: translate(50%, -50%);

    background-color: #146ef5;

    transition: width 0.2s, height 0.2s;
  }

  ${Progress}:hover &::before {
    width: 12px;
    height: 12px;
  }
`

/**
 * &.seeking
 */
const CurrentTimeOverProgressBar = styled.div`
  --scale: 0;
  --translate-y: 0;

  position: absolute;

  top: 0;
  left: 100%;

  transform: translate(-50%, var(--translate-y)) scale(var(--scale));

  font-family: ${poppins.string};
  font-size: 10px;
  line-height: 20px;
  color: #222222;

  transition: transform 0.2s;
  pointer-events: none;

  ${Progress}:hover &, &.seeking {
    --scale: 1;
    --translate-y: -100%;
  }
`

const Duration = styled.div`
  margin-right: 20px;

  font-family: ${poppins.string};
  font-size: 13px;
  line-height: 20px;
  color: #808080;
`
